import { getI18nPageProps } from "@utils/translates";
import type { PageWithLayout, PageProps } from "@types";
import type { Meta } from "@layouts/page";
import Page from "@layouts/page";
import { useTranslations } from "next-intl";
import { getLayout as getDefaultLayout } from "@layouts/default-layout";
import { useRouter } from "next/router";
import NotFound from "pages/404";
import FallingLoading from "legacy/elements/fallingLoading";
import { GetServerSideProps } from "next";
import { resolveAndMapFallbackDataAsync } from "@utils/api";
import formApi from "@api/form";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";
import Auth, { forwardNotAuthenticated } from "@modules/auth";
import { setContext } from "@utils/Cookies";
import dynamic from "next/dynamic";

const Form = dynamic(() => import("@templates/form-page/FormPageTemplate"), {
  ssr: false,
});

interface Props extends PageProps {}

const FormPage: PageWithLayout<Props> = ({}) => {
  const t = useTranslations("meta");

  const { query } = useRouter();
  const slug = Array.isArray(query.slug) ? query.slug.join("/") : "";
  const { data = null, error } = useRequestImmutable(formApi.getForm(slug));

  if (error?.error?.code === 404) return <NotFound />;

  const content = !data ? (
    <FallingLoading fullScreen />
  ) : (
    <Form data={data.data} />
  );

  if (error?.unAuthorizedRequest)
    return (
      <Auth
        redirectUrl={`/register?next=${encodeURIComponent("/form/" + slug)}`}
        redirectHandler={forwardNotAuthenticated}
      >
        {content}
      </Auth>
    );

  const meta: Meta = {
    title: data?.data.page.seo_title || data?.data.page.name || t("title"),
    description: data?.data.page.seo_description || t("desc"),
  };

  return <Page meta={meta}>{content}</Page>;
};
FormPage.getLayout = getDefaultLayout;

export default FormPage;

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const i18nProps = await getI18nPageProps(ctx, "form");
  const { query, req } = ctx;
  setContext(ctx);

  const slug = Array.isArray(query.slug) ? query.slug.join("/") : "";
  const ssrActivated =
    String(req.headers?.["x-ssr-activate"]).toLowerCase() !== "false";
  if (!ssrActivated) {
    return { props: i18nProps };
  }

  try {
    const fallback = await resolveAndMapFallbackDataAsync(
      formApi.getForm(slug)
    );
    return { props: { ...i18nProps, fallback } };
  } catch (err) {
    if ((err as any).unAuthorizedRequest) {
      return { props: i18nProps };
    }
    return {
      notFound: true,
      props: {},
    };
  }
};
